import { AxiosInstance } from 'axios';
import { axiosInstance } from './axios-instance';
import { AuthError } from './auth-api';

interface Test {
  id: number;
  title: string;
  slug: string; // <-- ADDED THIS PROPERTY
  type: string;
  structure: string;
  picture: string;
  description: string;
  price: number;
  price_currency: string;
  number_of_questions: number;
  available_time: number;
}
export interface AvailableTestsParams {
  structure?: string;
  type?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface AnswerOption {
  id: number;
  display_text: string;
  image: string | null;
  is_correct: boolean;
  order_in_question: number;
}

export interface Question {
  id: number;
  text: string;
  hint?: string;
  reasoning: string;
  image?: string;
  difficulty: number;
  topic: string;
  type: string;
  answers: AnswerOption[];
  points_gain: number;
  points_loss: number;
  purchase_type:string;
  question_texts: string;
}

export interface TestMetadata {
  test_id: number;
  test_name: string;
  question_count: number;
  time_limit_minutes: number;
  questions: Question[];
  status?: string; // For async exam building: "building_started", "building", "completed", "error"
  message?: string;
  action?: string;
  poll_url?: string;
  poll_interval?: number;
  estimated_time?: string;
  progress?: number;
}

export interface AnswerSubmission {
  question_id: number;
  selected_answer_ids: number[];
}

export interface ExamFinishContext {
  currentQuestionPosition: number;
  currentQuestionId: number;
}

export class TestsApi {
  private api: AxiosInstance;

  constructor(api: AxiosInstance = axiosInstance) {
    this.api = api;
  }

  async getAvailableTests(params?: AvailableTestsParams): Promise<any> { 
    try {
      const cleanParams = params ? Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== '')
      ) : {};
  
      const response = await this.api.get('/api/available-tests/', { 
        params: cleanParams 
      });
  
      return response.data; // Return the full response data
    } catch (error: any) {
      console.error('TestsApi: Error getting available tests:', error);
      if (error.response) {
        throw {
          detail: error.response.data.detail || 'Ошибка получения доступных тестов',
          code: error.response.data.code,
          status_code: error.response.status
        } as AuthError;
      }
      throw { detail: 'Ошибка сети' } as AuthError;
    }
  }
  

  async getOwnedTests(params?: AvailableTestsParams): Promise<any> { // Return type is any to match useTests hook
    try {
      
      // Clean params - remove undefined values
      const cleanParams = params ? Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== '')
      ) : {};
      const response = await this.api.get('/api/owned-tests/', { 
        params: cleanParams 
      });
      
      return response.data;
    } catch (error: any) {
      console.error('TestsApi: Error getting owned tests:', error);
      if (error.response) {
        throw {
          detail: error.response.data.detail || 'Ошибка получения купленных тестов',
          code: error.response.data.code,
          status_code: error.response.status
        } as AuthError;
      }
      throw { detail: 'Ошибка сети' } as AuthError;
    }
  }

  // Method to search tests with pagination support
  async searchTests(query: string, params?: { type?: string; structure?: string; page?: number; limit?: number }): Promise<{ tests: Test[]; total: number; hasMore: boolean }> {
    try {
      const searchParams = {
        search: query,
        ...params
      };

      const cleanParams = Object.fromEntries(
        Object.entries(searchParams).filter(([_, value]) => value !== undefined && value !== '')
      );

      const response = await this.api.get('/api/search-tests/', { 
        params: cleanParams 
      });
      
      return response.data;
    } catch (error: any) {
      console.error('TestsApi: Error searching tests:', error);
      if (error.response) {
        throw {
          detail: error.response.data.detail || 'Ошибка поиска тестов',
          code: error.response.data.code,
          status_code: error.response.status
        } as AuthError;
      }
      throw { detail: 'Ошибка сети' } as AuthError;
    }
  }

  async getTestMetadata(testPk: number, pId: number, testMode: string): Promise<TestMetadata> {
    try {
      const endpoint =
        testMode === "Eкзамен"
          ? `/api/exam-meta/${pId}/`
          : `/api/test-meta/${pId}/`;

      // Use the same axiosInstance as all other working endpoints
      const apiInstance = axiosInstance;

      console.log(`🚀 Making ${testMode} request to:`, apiInstance.defaults.baseURL + endpoint);
      
      const response = await apiInstance.get<TestMetadata>(endpoint);
      
      // Debug: Log the response for exam mode
      if (testMode === "Eкзамен") {
        console.log('🔍 EXAM Response status:', response.status);
        console.log('🔍 EXAM Response headers:', response.headers);
        console.log('🔍 EXAM Response content-type:', response.headers['content-type']);
        
        // Check if response is HTML instead of JSON
        if (typeof response.data === 'string' && (response.data as string).includes('<html>')) {
          console.error('❌ EXAM Response is HTML instead of JSON!');
          console.error('HTML content preview:', (response.data as string).substring(0, 200) + '...');
          throw new Error('Server returned HTML instead of JSON - API proxy not working');
        }
        
        console.log('🔍 EXAM Response data keys:', Object.keys(response.data));
        console.log('🔍 EXAM Full response size:', JSON.stringify(response.data).length, 'bytes');
        console.log('🔍 EXAM Response data preview:', {
          status: response.data.status,
          hasQuestions: !!response.data.questions,
          questionCount: response.data.questions?.length
        });
      }
      
      // Handle async exam building for "Eкзамен" mode
      if (testMode === "Eкзамен" && response.data.status === "building_started") {
        console.log('✅ Exam building started, polling for completion...');
        return this.pollExamStatus(pId);
      }
      
      // If we get here in exam mode, it means building is complete
      if (testMode === "Eкзамен") {
        console.log('✅ Exam building already complete, returning data directly');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('TestsApi: Error getting test metadata:', error);
      if (error.response) {
        throw {
          detail: error.response.data.detail || 'Failed to load test data',
          code: error.response.data.code,
          status_code: error.response.status
        } as AuthError;
      }
      throw { detail: 'Network error' } as AuthError;
    }
  }

  async pollExamStatus(pId: number, maxAttempts: number = 90): Promise<TestMetadata> {
    console.log('Starting exam status polling...');
    
    // Create admin API instance for polling (ensures API proxy works)
    const adminApiInstance = axiosInstance;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Progressive delay: start with 1s, then 2s, then 3s (max 5s)
        const delay = Math.min(1000 + (attempt * 500), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
        
        console.log(`🔄 Poll attempt ${attempt + 1}: Requesting`, adminApiInstance.defaults.baseURL + `/api/exam-status/${pId}/`);
        const response = await adminApiInstance.get(`/api/exam-status/${pId}/`);
        
        // Check if response is HTML instead of JSON
        if (typeof response.data === 'string' && (response.data as string).includes('<html>')) {
          console.error('❌ POLL Response is HTML instead of JSON!');
          console.error('HTML content preview:', (response.data as string).substring(0, 200) + '...');
          throw new Error('Server returned HTML instead of JSON during polling - API proxy not working');
        }
        
        const data = response.data;
        console.log(`Poll attempt ${attempt + 1}: Status = ${data.status}, Progress = ${data.progress}%`);
        
        if (data.status === "building") {
          // Update UI with progress if available
          if (data.progress !== undefined) {
            // Emit progress event for UI updates
            console.log(`Exam building progress: ${data.progress}% - ${data.message}`);
          }
          continue; // Keep polling
        }
        
        if (data.status === "error") {
          throw new Error(data.message || 'Exam building failed');
        }
        
        // Handle completed status - fetch the actual exam data
        if (data.status === "completed") {
          console.log('✅ Exam building completed! Fetching exam data...');
          
          // The completed status should contain exam_cache_key or attempt_id
          if (data.exam_cache_key) {
            console.log('📋 Using exam_cache_key to fetch data...');
            // Try to fetch the cached exam data
            try {
              const examDataResponse = await adminApiInstance.get(`/api/exam-meta/${pId}/`);
              if (examDataResponse.data.questions && Array.isArray(examDataResponse.data.questions)) {
                console.log('✅ Retrieved exam data from cache successfully!');
                return examDataResponse.data;
              }
            } catch (cacheError) {
              console.warn('⚠️ Failed to fetch from cache, trying direct polling...', cacheError);
            }
          }
          
          // Continue polling for a few more attempts to get the data
          console.log('⏳ Continuing polling for exam data...');
          continue;
        }
        
        // Check if we have valid exam data regardless of status (handles race conditions)
        if (data.questions && Array.isArray(data.questions) && data.questions.length > 0) {
          console.log('Exam building completed successfully!');
          console.log('✅ Exam data structure validated:', {
            hasQuestions: !!data.questions,
            questionCount: data.questions?.length,
            dataKeys: Object.keys(data)
          });
          return data;
        }
        
        if (data.status === "expired") {
          // If we get expired but it's late in the polling process, this might be a race condition
          // Try one more time before giving up
          if (attempt >= 5) { // Only after several attempts
            console.log('Got expired status, but retrying due to potential race condition...');
            continue;
          }
          throw new Error('Exam building expired, please try again');
        }
        
        // If we get here, status indicates completed but no valid data
        console.log('Status indicates completion but no valid exam data, retrying...');
        continue;
        
      } catch (error: any) {
        if (error.response?.status === 404) {
          // Building status not found, maybe completed or expired
          console.log('Building status not found, retrying...');
          continue;
        }
        throw error;
      }
    }
    
    throw new Error(`Exam building timeout after ${Math.round(maxAttempts * 3)} seconds. Please try again.`);
  }

  
  async submitTestAnswers(
    testPk: number,
    pId: number,
    testMode: string,
    answers: AnswerSubmission[],
    finishContext?: ExamFinishContext
  ): Promise<any> {
    try {
      const endpoint =
        testMode === "Eкзамен"
          ? `/api/exam-meta/${pId}/`
          : `/api/test-meta/${pId}/`;

      // Use the same axiosInstance as all other working endpoints
      const apiInstance = this.api;

      const payload =
        testMode === "Eкзамен" && finishContext
          ? {
              answers,
              current_question_position: finishContext.currentQuestionPosition,
              current_question_id: finishContext.currentQuestionId,
              finish_question_id: finishContext.currentQuestionId,
            }
          : { answers };

      const response = await apiInstance.post(endpoint, payload);
      
      // Debug: Log the response to see what we're getting
      console.log('Submit response:', response.data);
      console.log('Test mode:', testMode);
      console.log('Response status:', response.data.status);
      
      // Handle async results processing for "Eкзамен" mode
      if (testMode === "Eкзамен" && response.data.status === "processing_started") {
        console.log('Exam results processing started, polling for completion...');
        return this.pollExamResults(pId);
      }
      
      return response.data;
    } catch (error: any) {
      console.error('TestsApi: Error submitting answers:', error);
      if (error.response) {
        throw {
          detail: error.response.data.detail || 'Failed to submit answers',
          code: error.response.data.code,
          status_code: error.response.status
        } as AuthError;
      }
      throw { detail: 'Network error' } as AuthError;
    }
  }

  async pollExamResults(pId: number, maxAttempts: number = 60): Promise<any> {
    console.log('Starting exam results polling...');
    
    // Create admin API instance for polling (ensures API proxy works)
    const adminApiInstance = axiosInstance;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
        
        const response = await adminApiInstance.get(`/api/exam-results-status/${pId}/`);
        const data = response.data;
        
        console.log(`Results poll attempt ${attempt + 1}: Status = ${data.status}, Progress = ${data.progress}%`);
        
        if (data.status === "processing") {
          // Update UI with progress if available
          if (data.progress !== undefined) {
            // Emit progress event for UI updates
            console.log(`Results processing progress: ${data.progress}% - ${data.message}`);
          }
          continue; // Keep polling
        }
        
        if (data.status === "error") {
          throw new Error(data.message || 'Results processing failed');
        }
        
        if (data.status === "expired") {
          throw new Error('Results processing expired, please retake exam');
        }
        
        // Results ready! Return the complete results data
        console.log('Results processing completed successfully!');
        
        // For results, we expect different data structure - could be redirect response
        // Let's just return what we get since results page will handle the redirect
        return data;
        
      } catch (error: any) {
        if (error.response?.status === 404) {
          // Processing status not found, maybe completed or expired
          console.log('Processing status not found, retrying...');
          continue;
        }
        throw error;
      }
    }
    
    throw new Error(`Results processing timeout after ${maxAttempts * 2} seconds`);
  }
  async getTestInfo(slug:string){
    const response = await axiosInstance.get(`/api/test-info/${slug}/`)
    return response
  }
}

export default new TestsApi();
